import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, filter, take } from 'rxjs';
import { ExternalDataService, ScrapItemPriceDto, ScrapCategoryDto, PricingGroup, PricingGroupItem, PricingType } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { CreateScrapItemComponent } from '../scrap-collection/create-scrap-item/create-scrap-item.component';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

@Component({
  selector: 'app-scrap-pricing',
  standalone: true,
  imports: [CommonModule, FormsModule, CreateScrapItemComponent],
  templateUrl: './scrap-pricing.component.html',
  styleUrl: './scrap-pricing.component.css'
})
export class ScrapPricingComponent implements OnInit, OnDestroy {
  scrapItems: ScrapItemPriceDto[] = [];
  filteredItems: ScrapItemPriceDto[] = [];
  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';
  selectedCategory: string = 'all';
  searchTerm: string = '';
  viewMode: 'global' | 'groups' = 'global';
  pricingGroups: PricingGroup[] = [];
  selectedPricingGroup: PricingGroup | null = null;
  showCreateScrapItemModal = false;
  categories: ScrapCategoryDto[] = [];
  // Delete modal state
  showDeleteModal = false;
  itemPendingDelete: ScrapItemPriceDto | null = null;
  Math = Math; // Make Math available in template
  // Export functionality
  selectedItems = new Set<string>(); // Track selected item IDs
  includeDealerPrice = false; // Checkbox for including dealer price
  exportingPdf = false; // Track PDF export state
  overrideStates: { [itemId: string]: { 
    overridden: boolean, 
    pricingType: PricingType, 
    value: number, 
    dealerPercentage: number | null,
    isActive: boolean,
    isVisibleToCustomer: boolean
  } } = {};
  originalOverrideStatesJson = '';
  loadingGroups = false;
  PricingType = PricingType; // Make PricingType enum available in template
  private destroy$ = new Subject<void>();
  private imageLoadStates = new Map<string, boolean>(); // Track image load states
  private originalValues = new Map<string, { regularPrice: number; specialPrice: number; isActive: boolean; isFeatured: boolean; dealerPercentage: number; scrapName: string; unit: string; isScrap: boolean; isVisibleToCustomer: boolean; sortOrder: number; imageName: string; scrapCategory: string; alternativeNames: string[] | null }>(); // Track original values

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // Wait for authentication to be ready before loading data
    this.authService.authReady$
      .pipe(
        filter(ready => ready === true),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadScrapPrices();
        this.loadCategories();
        this.loadPricingGroups();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadScrapPrices(): void {
    this.loading = true;
    this.errorMessage = '';

    this.externalDataService.getScrapPriceList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.scrapItems = data;
          this.syncCategoryIds();
          // Store original values for change tracking
          this.storeOriginalValues();
          if (this.selectedPricingGroup) {
            this.initializeOverrideStates(this.selectedPricingGroup);
          }
          this.applyFilter();
          this.loading = false;
          this.successMessage = ''; // Clear any previous success messages
        },
        error: (error) => {
          console.error('Error loading scrap prices:', error);

          // Provide more specific error messages based on the error type
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view pricing data.';
          } else if (error.status === 404) {
            this.errorMessage = 'Pricing data not found. Please contact support.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to load scrap prices. Please try again.';
          }

          this.loading = false;
        }
      });
  }

  loadCategories(): void {
    this.externalDataService.getScrapCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.categories = data;
          this.syncCategoryIds();
          // Re-baseline originals now that names are corrected from the category list
          if (this.scrapItems.length) this.storeOriginalValues();
        },
        error: (err) => { console.error('Error loading categories:', err); }
      });
  }

  private syncCategoryIds(): void {
    if (!this.categories.length) return;
    this.scrapItems.forEach(item => {
      if (item.scrapCategoryId) {
        // ID is authoritative — derive the display name from the categories list
        const match = this.categories.find(c => c.id === item.scrapCategoryId);
        if (match) item.scrapCategory = match.name;
      } else {
        // No ID yet — look up by name and back-fill the ID
        const match = this.categories.find(c => c.name === item.scrapCategory);
        if (match) item.scrapCategoryId = match.id;
      }
    });
  }

  onItemCategoryChange(item: ScrapItemPriceDto): void {
    // scrapCategory is already updated by [(ngModel)]; just sync the ID
    const match = this.categories.find(c => c.name === item.scrapCategory);
    item.scrapCategoryId = match?.id ?? null;
  }

  private storeOriginalValues(): void {
    this.originalValues.clear();
    this.scrapItems.forEach(item => {
      this.originalValues.set(item.scrapItemId, {
        regularPrice: item.regularPrice,
        specialPrice: item.specialPrice,
        isActive: item.isActive,
        isFeatured: item.isFeatured,
        dealerPercentage: item.dealerPercentage,
        scrapName: item.scrapName,
        unit: item.unit,
        isScrap: item.isScrap,
        isVisibleToCustomer: item.isVisibleToCustomer,
        sortOrder: item.sortOrder,
        imageName: item.imageName,
        scrapCategory: item.scrapCategory,
        alternativeNames: item.alternativeNames ? [...item.alternativeNames] : null
      });
    });
  }

  getUniqueCategories(): string[] {
    const categories = [...new Set(this.scrapItems.map(item => item.scrapCategory))];
    return categories.sort();
  }

  onCategoryChange(category: string): void {
    this.selectedCategory = category;
    this.applyFilter();
  }

  applyFilter(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    const byCategory = (item: ScrapItemPriceDto) => this.selectedCategory === 'all' || item.scrapCategory === this.selectedCategory;
    const byName = (item: ScrapItemPriceDto) => term.length === 0 || (item.scrapName || '').toLowerCase().includes(term);
    this.filteredItems = this.scrapItems.filter(item => byCategory(item) && byName(item));
  }

  getFilteredItems(): ScrapItemPriceDto[] {
    return this.filteredItems;
  }

  areAlternativeNamesEqual(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
  }

  getCategoryCount(category: string): number {
    return this.scrapItems.filter(item => item.scrapCategory === category).length;
  }

  updatePrices(): void {
    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Only include items that have been modified (including name and unit)
    const modifiedItems = this.scrapItems.filter(item => this.isItemModified(item));

    if (modifiedItems.length === 0) {
      this.successMessage = 'No changes detected. All prices are up to date.';
      this.saving = false;
      return;
    }

    const updatedPrices = modifiedItems.map(item => ({
      scrapItemId: item.scrapItemId,
      scrapName: item.scrapName,
      unit: item.unit,
      scrapCategory: item.scrapCategory,
      scrapCategoryId: item.scrapCategoryId ?? null,
      regularPrice: item.regularPrice,
      specialPrice: item.specialPrice,
      isActive: item.isActive,
      isFeatured: item.isFeatured,
      dealerPercentage: item.dealerPercentage,
      customerPrice: parseFloat((item.regularPrice - (item.regularPrice * item.dealerPercentage / 100)).toFixed(2)),
      isScrap: item.isScrap,
      isVisibleToCustomer: item.isVisibleToCustomer,
      sortOrder: item.sortOrder,
      imageName: item.imageName,
      alternativeNames: item.alternativeNames && item.alternativeNames.length > 0 ? item.alternativeNames : null
    }));

    this.externalDataService.updateScrapPrices(updatedPrices)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = `Successfully updated ${modifiedItems.length} price(s)!`;
          this.saving = false;
          // Update original values after successful save
          this.storeOriginalValues();
        },
        error: (error) => {
          this.errorMessage = 'Failed to update prices. Please try again.';
          this.saving = false;
          console.error('Error updating prices:', error);
        }
      });
  }

  resetPrices(): void {
    this.loadScrapPrices();
    this.successMessage = '';
    this.errorMessage = '';
  }

  isFormValid(): boolean {
    return this.scrapItems.every(item =>
      !isNaN(item.regularPrice) &&
      !isNaN(item.specialPrice)
    );
  }

  hasChanges(): boolean {
    return this.scrapItems.some(item => {
      return this.isItemModified(item);
    });
  }

  getModifiedCount(): number {
    return this.scrapItems.filter(item => this.isItemModified(item)).length;
  }

  isItemModified(item: ScrapItemPriceDto): boolean {
    const original = this.originalValues.get(item.scrapItemId);
    if (!original) return false;
    return item.regularPrice !== original.regularPrice ||
      item.specialPrice !== original.specialPrice ||
      item.isActive !== original.isActive ||
      item.isFeatured !== original.isFeatured ||
      item.dealerPercentage !== original.dealerPercentage ||
      item.scrapName !== original.scrapName ||
      item.unit !== original.unit ||
      item.isScrap !== original.isScrap ||
      item.isVisibleToCustomer !== original.isVisibleToCustomer ||
      item.sortOrder !== original.sortOrder ||
      item.imageName !== original.imageName ||
      item.scrapCategory !== original.scrapCategory ||
      !this.areAlternativeNamesEqual(item.alternativeNames, original.alternativeNames);
  }

  trackByScrapItemId(index: number, item: ScrapItemPriceDto): string {
    return item.scrapItemId;
  }

  addAlternativeNameFromInput(item: ScrapItemPriceDto, event: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = event.target.value.trim();
      if (value) {
        if (!item.alternativeNames) {
          item.alternativeNames = [];
        }
        if (!item.alternativeNames.includes(value)) {
          item.alternativeNames.push(value);
        }
        event.target.value = '';
      }
    }
  }

  addAlternativeName(item: ScrapItemPriceDto, inputElement: HTMLInputElement) {
    const value = inputElement.value.trim();
    if (value) {
      if (!item.alternativeNames) {
        item.alternativeNames = [];
      }
      if (!item.alternativeNames.includes(value)) {
        item.alternativeNames.push(value);
      }
      inputElement.value = '';
    }
  }

  removeAlternativeName(item: ScrapItemPriceDto, index: number) {
    if (item.alternativeNames) {
      item.alternativeNames.splice(index, 1);
      // Let it be an empty array if empty, length will be 0, mapping handles if it should be null
    }
  }

  isValueNaN(value: number): boolean {
    return isNaN(value);
  }

  isValidImageUrl(url: string): boolean {
    if (!url || url.trim() === '') return false;

    // Check if it's a valid URL format
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  shouldShowFallback(item: ScrapItemPriceDto): boolean {
    if (!this.isValidImageUrl(item.imageName)) {
      return true;
    }

    // Check if image failed to load
    return this.imageLoadStates.get(item.scrapItemId) === false;
  }

  onImageError(event: any, item: ScrapItemPriceDto): void {
    this.imageLoadStates.set(item.scrapItemId, false);
  }

  onImageLoad(event: any, item: ScrapItemPriceDto): void {
    this.imageLoadStates.set(item.scrapItemId, true);
  }

  getFallbackStyle(item: ScrapItemPriceDto): string {
    // Use the item ID to consistently assign a fallback style
    const hash = item.scrapItemId.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    const styles = ['fallback-image', 'fallback-image-alt-1', 'fallback-image-alt-2', 'fallback-image-alt-3', 'fallback-image-alt-4'];
    return styles[Math.abs(hash) % styles.length];
  }

  openCreateScrapItemModal() {
    this.showCreateScrapItemModal = true;
  }
  closeCreateScrapItemModal() {
    this.showCreateScrapItemModal = false;
  }
  onScrapItemCreated(): void {
    this.showCreateScrapItemModal = false;
    this.loadScrapPrices();
  }

  // Delete flow
  requestDelete(item: ScrapItemPriceDto) {
    this.itemPendingDelete = item;
    this.showDeleteModal = true;
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.itemPendingDelete = null;
  }

  confirmDelete() {
    if (!this.itemPendingDelete) return;
    const id = this.itemPendingDelete.scrapItemId;
    this.externalDataService.deleteScrapItem(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Remove from local arrays
          this.scrapItems = this.scrapItems.filter(x => x.scrapItemId !== id);
          this.selectedItems.delete(id);
          this.applyFilter();
          this.cancelDelete();
        },
        error: () => {
          alert('Failed to delete scrap item');
        }
      });
  }

  // Selection functionality
  toggleSelection(itemId: string): void {
    if (this.selectedItems.has(itemId)) {
      this.selectedItems.delete(itemId);
    } else {
      this.selectedItems.add(itemId);
    }
  }

  isSelected(itemId: string): boolean {
    return this.selectedItems.has(itemId);
  }

  selectAll(): void {
    const filtered = this.getFilteredItems();
    if (this.selectedItems.size === filtered.length) {
      // Deselect all
      this.selectedItems.clear();
    } else {
      // Select all filtered items
      filtered.forEach(item => this.selectedItems.add(item.scrapItemId));
    }
  }

  getSelectedCount(): number {
    return this.selectedItems.size;
  }

  // PDF Export functionality
  async exportToPdf(): Promise<void> {
    this.exportingPdf = true;
    this.errorMessage = '';

    try {
      // Determine which items to export
      let itemsToExport: ScrapItemPriceDto[];
      if (this.selectedItems.size > 0) {
        // Export only selected items
        itemsToExport = this.scrapItems.filter(item => this.selectedItems.has(item.scrapItemId));
      } else {
        // Export all filtered items
        itemsToExport = this.getFilteredItems();
      }

      if (itemsToExport.length === 0) {
        this.errorMessage = 'No items to export.';
        this.exportingPdf = false;
        return;
      }

      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const { width, height } = page.getSize();

      // Load fonts
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Define margins
      const margin = 50;
      const tableTop = height - margin - 50;
      const tableWidth = width - 2 * margin;

      // Title
      page.drawText('Scrap Pricing List', {
        x: margin,
        y: tableTop + 20,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      // Date
      const dateStr = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      page.drawText(`Generated on: ${dateStr}`, {
        x: margin,
        y: tableTop + 5,
        size: 10,
        font: font,
        color: rgb(0.5, 0.5, 0.5),
      });

      // Define column headers (using Rs instead of ₹ for PDF compatibility)
      // Customer Price is always shown at the end
      const baseHeaders = ['Category', 'Name', 'Unit', 'Customer Price (Rs)'];
      const headers = this.includeDealerPrice
        ? ['Category', 'Name', 'Unit', 'Regular Price (Rs)', 'Dealer %', 'Customer Price (Rs)']
        : baseHeaders;

      // Calculate column widths with custom proportions (Unit is narrower)
      const colCount = headers.length;
      // Define width ratios: Category=1.2, Name=1.5, Unit=0.6, Price columns=1.0 each
      let widthRatios: number[];
      if (this.includeDealerPrice) {
        // Category, Name, Unit, Regular Price, Dealer %, Customer Price
        widthRatios = [1.2, 1.5, 0.6, 1.0, 0.8, 1.0];
      } else {
        // Category, Name, Unit, Customer Price
        widthRatios = [1.2, 1.5, 0.6, 1.0];
      }
      const totalRatio = widthRatios.reduce((sum, ratio) => sum + ratio, 0);
      const colWidths = widthRatios.map(ratio => (tableWidth * ratio) / totalRatio);

      const rowHeight = 20;
      const headerHeight = 30; // Increased to accommodate wrapped text

      // Starting position
      let currentY = tableTop;

      // Draw table header
      page.drawRectangle({
        x: margin,
        y: currentY - headerHeight,
        width: tableWidth,
        height: headerHeight,
        color: rgb(0.9, 0.9, 0.9),
      });

      // Helper function to wrap text for headers
      const wrapHeaderText = (text: string, maxWidth: number, fontSize: number): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const testWidth = boldFont.widthOfTextAtSize(testLine, fontSize);

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          lines.push(currentLine);
        }
        return lines.length > 0 ? lines : [text];
      };

      // Draw header text and borders
      let currentX = margin;
      headers.forEach((header, index) => {
        const colWidth = colWidths[index];
        const x = currentX;

        // Wrap header text if needed
        const maxTextWidth = colWidth - 10;
        const wrappedLines = wrapHeaderText(header, maxTextWidth, 10);
        const lineHeight = 10;
        const startY = currentY - headerHeight + (headerHeight - (wrappedLines.length * lineHeight)) / 2 + lineHeight;

        // Draw wrapped header text
        wrappedLines.forEach((line, lineIndex) => {
          page.drawText(line, {
            x: x + 5,
            y: startY - (lineIndex * lineHeight),
            size: 10,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
        });

        // Vertical border
        if (index > 0) {
          page.drawLine({
            start: { x, y: currentY },
            end: { x, y: currentY - headerHeight },
            thickness: 0.5,
            color: rgb(0.7, 0.7, 0.7),
          });
        }

        currentX += colWidth;
      });

      // Draw horizontal border below header
      page.drawLine({
        start: { x: margin, y: currentY - headerHeight },
        end: { x: margin + tableWidth, y: currentY - headerHeight },
        thickness: 1,
        color: rgb(0, 0, 0),
      });

      currentY -= headerHeight;

      // Draw table rows
      let rowIndex = 0;
      for (const item of itemsToExport) {
        // Check if we need a new page
        if (currentY < margin + rowHeight) {
          page = pdfDoc.addPage([595.28, 841.89]);
          currentY = height - margin;

          // Redraw header on new page
          page.drawRectangle({
            x: margin,
            y: currentY - headerHeight,
            width: tableWidth,
            height: headerHeight,
            color: rgb(0.9, 0.9, 0.9),
          });

          // Redraw header with word wrapping on new page
          let headerX = margin;
          headers.forEach((header, index) => {
            const colWidth = colWidths[index];
            const x = headerX;

            // Wrap header text if needed
            const maxTextWidth = colWidth - 10;
            const wrappedLines = wrapHeaderText(header, maxTextWidth, 10);
            const lineHeight = 10;
            const startY = currentY - headerHeight + (headerHeight - (wrappedLines.length * lineHeight)) / 2 + lineHeight;

            // Draw wrapped header text
            wrappedLines.forEach((line, lineIndex) => {
              page.drawText(line, {
                x: x + 5,
                y: startY - (lineIndex * lineHeight),
                size: 10,
                font: boldFont,
                color: rgb(0, 0, 0),
              });
            });

            if (index > 0) {
              page.drawLine({
                start: { x, y: currentY },
                end: { x, y: currentY - headerHeight },
                thickness: 0.5,
                color: rgb(0.7, 0.7, 0.7),
              });
            }

            headerX += colWidth;
          });

          page.drawLine({
            start: { x: margin, y: currentY - headerHeight },
            end: { x: margin + tableWidth, y: currentY - headerHeight },
            thickness: 1,
            color: rgb(0, 0, 0),
          });

          currentY -= headerHeight;
        }

        // Calculate customer price
        const customerPrice = item.regularPrice - (item.regularPrice * item.dealerPercentage / 100);

        // Prepare row data - Customer Price is always at the end
        let rowData: string[];
        if (this.includeDealerPrice) {
          rowData = [
            item.scrapCategory || '',
            item.scrapName || '',
            item.unit || '',
            item.regularPrice.toFixed(2),
            item.dealerPercentage.toFixed(2) + '%',
            customerPrice.toFixed(2),
          ];
        } else {
          rowData = [
            item.scrapCategory || '',
            item.scrapName || '',
            item.unit || '',
            customerPrice.toFixed(2),
          ];
        }

        // Draw row data using custom column widths
        let rowX = margin;
        rowData.forEach((cell, index) => {
          const colWidth = colWidths[index];
          const x = rowX;
          const cellText = String(cell);

          // Truncate text if too long
          let displayText = cellText;
          const maxWidth = colWidth - 10;
          const textWidth = font.widthOfTextAtSize(cellText, 9);
          if (textWidth > maxWidth) {
            // Truncate and add ellipsis
            let truncated = cellText;
            while (font.widthOfTextAtSize(truncated + '...', 9) > maxWidth && truncated.length > 0) {
              truncated = truncated.slice(0, -1);
            }
            displayText = truncated + '...';
          }

          page.drawText(displayText, {
            x: x + 5,
            y: currentY - rowHeight + 7,
            size: 9,
            font: font,
            color: rgb(0, 0, 0),
          });

          // Vertical border
          if (index > 0) {
            page.drawLine({
              start: { x, y: currentY },
              end: { x, y: currentY - rowHeight },
              thickness: 0.5,
              color: rgb(0.7, 0.7, 0.7),
            });
          }

          rowX += colWidth;
        });

        // Horizontal border
        page.drawLine({
          start: { x: margin, y: currentY - rowHeight },
          end: { x: margin + tableWidth, y: currentY - rowHeight },
          thickness: 0.5,
          color: rgb(0.7, 0.7, 0.7),
        });

        currentY -= rowHeight;
        rowIndex++;
      }

      // Save PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileName = `scrap-pricing-${timestamp}.pdf`;
      link.download = fileName;
      link.click();

      // Cleanup
      URL.revokeObjectURL(url);
      this.successMessage = `Successfully exported ${itemsToExport.length} item(s) to PDF.`;
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.errorMessage = 'Failed to generate PDF. Please try again.';
    } finally {
      this.exportingPdf = false;
    }
  }

  // ── Pricing Groups Management ───────────────────────────────────

  loadPricingGroups(): void {
    this.loadingGroups = true;
    this.externalDataService.getPricingGroups()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.pricingGroups = data;
          if (data.length > 0 && !this.selectedPricingGroup) {
            this.selectPricingGroup(data[0]);
          }
          this.loadingGroups = false;
        },
        error: (err) => {
          console.error('Error loading pricing groups:', err);
          this.errorMessage = 'Failed to load pricing groups.';
          this.loadingGroups = false;
        }
      });
  }

  selectPricingGroup(group: PricingGroup): void {
    this.selectedPricingGroup = group;
    this.successMessage = '';
    this.errorMessage = '';
    this.initializeOverrideStates(group);
  }

  compareGroups(c1: any, c2: any): boolean {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  initializeOverrideStates(group: PricingGroup): void {
    this.overrideStates = {};
    this.scrapItems.forEach(item => {
      const override = group.items.find(i => i.scrapItemId === item.scrapItemId);
      if (override) {
        this.overrideStates[item.scrapItemId] = {
          overridden: true,
          pricingType: override.pricingType,
          value: override.value,
          dealerPercentage: override.dealerPercentage !== undefined && override.dealerPercentage !== null ? override.dealerPercentage : null,
          isActive: override.isActive !== undefined && override.isActive !== null ? override.isActive : item.isActive,
          isVisibleToCustomer: override.isVisibleToCustomer !== undefined && override.isVisibleToCustomer !== null ? override.isVisibleToCustomer : item.isVisibleToCustomer
        };
      } else {
        this.overrideStates[item.scrapItemId] = {
          overridden: false,
          pricingType: PricingType.Percentage,
          value: 100,
          dealerPercentage: null,
          isActive: item.isActive,
          isVisibleToCustomer: item.isVisibleToCustomer
        };
      }
    });
    this.originalOverrideStatesJson = JSON.stringify(this.overrideStates);
  }

  hasGroupChanges(): boolean {
    if (!this.selectedPricingGroup) return false;
    return JSON.stringify(this.overrideStates) !== this.originalOverrideStatesJson;
  }

  getGroupModifiedCount(): number {
    if (!this.selectedPricingGroup) return 0;
    let count = 0;
    this.scrapItems.forEach(item => {
      const state = this.overrideStates[item.scrapItemId];
      const original = this.selectedPricingGroup?.items.find(i => i.scrapItemId === item.scrapItemId);
      if (state) {
        const wasOverridden = !!original;
        const isOverridden = state.overridden;
        if (wasOverridden !== isOverridden) {
          count++;
        } else if (isOverridden && original) {
          const originalDealerPct = original.dealerPercentage !== undefined && original.dealerPercentage !== null ? original.dealerPercentage : null;
          const stateDealerPct = state.dealerPercentage !== null && state.dealerPercentage !== undefined ? Number(state.dealerPercentage) : null;
          
          const originalIsActive = original.isActive !== undefined && original.isActive !== null ? original.isActive : item.isActive;
          const originalIsVisible = original.isVisibleToCustomer !== undefined && original.isVisibleToCustomer !== null ? original.isVisibleToCustomer : item.isVisibleToCustomer;

          if (state.pricingType !== original.pricingType || 
              state.value !== original.value ||
              stateDealerPct !== originalDealerPct ||
              state.isActive !== originalIsActive ||
              state.isVisibleToCustomer !== originalIsVisible) {
            count++;
          }
        }
      }
    });
    return count;
  }

  isOverrideModified(item: ScrapItemPriceDto): boolean {
    if (!this.selectedPricingGroup) return false;
    const state = this.overrideStates[item.scrapItemId];
    if (!state) return false;

    const original = this.selectedPricingGroup.items.find(i => i.scrapItemId === item.scrapItemId);
    if (!original) {
      return state.overridden;
    } else {
      const originalDealerPct = original.dealerPercentage !== undefined && original.dealerPercentage !== null ? original.dealerPercentage : null;
      const stateDealerPct = state.dealerPercentage !== null && state.dealerPercentage !== undefined ? Number(state.dealerPercentage) : null;
      
      const originalIsActive = original.isActive !== undefined && original.isActive !== null ? original.isActive : item.isActive;
      const originalIsVisible = original.isVisibleToCustomer !== undefined && original.isVisibleToCustomer !== null ? original.isVisibleToCustomer : item.isVisibleToCustomer;

      return !state.overridden || 
             state.pricingType !== original.pricingType || 
             state.value !== original.value ||
             stateDealerPct !== originalDealerPct ||
             state.isActive !== originalIsActive ||
             state.isVisibleToCustomer !== originalIsVisible;
    }
  }

  savePricingGroupChanges(): void {
    if (!this.selectedPricingGroup) return;
    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const items: PricingGroupItem[] = [];
    this.scrapItems.forEach(item => {
      const state = this.overrideStates[item.scrapItemId];
      if (state && state.overridden) {
        items.push({
          scrapItemId: item.scrapItemId,
          pricingType: state.pricingType,
          value: state.value,
          dealerPercentage: state.dealerPercentage !== null && state.dealerPercentage !== undefined && !isNaN(Number(state.dealerPercentage)) ? Number(state.dealerPercentage) : null,
          isActive: state.isActive,
          isVisibleToCustomer: state.isVisibleToCustomer
        });
      }
    });

    const payload = {
      name: this.selectedPricingGroup.name,
      items: items
    };

    this.externalDataService.updatePricingGroup(this.selectedPricingGroup.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = `Successfully updated pricing group "${this.selectedPricingGroup?.name}"!`;
          this.saving = false;
          if (this.selectedPricingGroup) {
            this.selectedPricingGroup.items = items;
            const index = this.pricingGroups.findIndex(g => g.id === this.selectedPricingGroup?.id);
            if (index !== -1) {
              this.pricingGroups[index].items = items;
            }
            this.initializeOverrideStates(this.selectedPricingGroup);
          }
        },
        error: (err) => {
          console.error('Error updating pricing group:', err);
          this.errorMessage = 'Failed to update pricing group changes.';
          this.saving = false;
        }
      });
  }





  getPreviewPrice(item: ScrapItemPriceDto): number {
    const state = this.overrideStates[item.scrapItemId];
    if (!state || !state.overridden) {
      return item.regularPrice;
    }
    if (state.pricingType === PricingType.Fixed) {
      return state.value;
    } else {
      const factor = state.value / 100;
      return Math.round(item.regularPrice * factor * 2) / 2;
    }
  }

  getPreviewCustomerPrice(item: ScrapItemPriceDto): number {
    const state = this.overrideStates[item.scrapItemId];
    const globalCustomerPrice = item.regularPrice - (item.regularPrice * item.dealerPercentage / 100);
    if (!state || !state.overridden) {
      return globalCustomerPrice;
    }

    let regularPrice = item.regularPrice;
    if (state.pricingType === PricingType.Fixed) {
      regularPrice = state.value;
    } else {
      const factor = state.value / 100;
      regularPrice = Math.round(item.regularPrice * factor * 2) / 2;
    }

    const dealerPct = state.dealerPercentage !== null && state.dealerPercentage !== undefined
      ? Number(state.dealerPercentage)
      : item.dealerPercentage;

    const rawCustomerPrice = regularPrice - (regularPrice * dealerPct / 100);
    return Math.round(rawCustomerPrice * 2) / 2;
  }

  /** Reverse-calculates the Override Value from a directly entered Preview Regular price. */
  onPreviewRegularPriceChange(item: ScrapItemPriceDto, rawValue: string | number, inputEl?: HTMLInputElement): void {
    const state = this.overrideStates[item.scrapItemId];
    const price = Number(rawValue);
    if (state && state.overridden && rawValue !== '' && !isNaN(price) && price >= 0) {
      if (state.pricingType === PricingType.Fixed) {
        state.value = price;
      } else if (item.regularPrice > 0) {
        const target = Math.round(price * 2) / 2;
        state.value = this.round(price / item.regularPrice * 100, 2);
        if (this.getPreviewPrice(item) !== target) {
          state.value = this.round(price / item.regularPrice * 100, 4);
        }
      }
    }
    if (inputEl) {
      inputEl.value = this.getPreviewPrice(item).toFixed(2);
    }
  }

  /** Reverse-calculates the Dealer % from a directly entered Preview Customer price. */
  onPreviewCustomerPriceChange(item: ScrapItemPriceDto, rawValue: string | number, inputEl?: HTMLInputElement): void {
    const state = this.overrideStates[item.scrapItemId];
    const price = Number(rawValue);
    const regularPrice = this.getPreviewPrice(item);
    if (state && state.overridden && rawValue !== '' && !isNaN(price) && regularPrice > 0) {
      const clamped = Math.min(Math.max(price, 0), regularPrice);
      state.dealerPercentage = this.round((1 - clamped / regularPrice) * 100, 2);
    }
    if (inputEl) {
      inputEl.value = this.getPreviewCustomerPrice(item).toFixed(2);
    }
  }

  getGlobalCustomerPrice(item: ScrapItemPriceDto): number {
    return item.regularPrice - (item.regularPrice * item.dealerPercentage / 100);
  }

  /** Reverse-calculates the global Dealer % from a directly entered Customer price. */
  onGlobalCustomerPriceChange(item: ScrapItemPriceDto, rawValue: string | number, inputEl?: HTMLInputElement): void {
    const price = Number(rawValue);
    if (rawValue !== '' && !isNaN(price) && item.regularPrice > 0) {
      const clamped = Math.min(Math.max(price, 0), item.regularPrice);
      item.dealerPercentage = this.round((1 - clamped / item.regularPrice) * 100, 2);
      if (this.getGlobalCustomerPrice(item).toFixed(2) !== clamped.toFixed(2)) {
        item.dealerPercentage = this.round((1 - clamped / item.regularPrice) * 100, 4);
      }
    }
    if (inputEl) {
      inputEl.value = this.getGlobalCustomerPrice(item).toFixed(2);
    }
  }

  private round(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  onViewModeChange(mode: 'global' | 'groups'): void {
    this.viewMode = mode;
    this.successMessage = '';
    this.errorMessage = '';
    if (mode === 'groups' && this.pricingGroups.length === 0) {
      this.loadPricingGroups();
    }
  }
}
